import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import Input from "./Input";
import { RegularExpressions } from "../constants/all-constant";
import { AppConfigurationHelper } from "../../services/AppConfigurationHelper";
import Heading from "./Heading";
import { DateFormatter } from "../utils/DateFormatter";
import { NewOffsetInput } from "./NewOffsetInput";
import Button from "./Button";

interface Field {
  label: string;
  key: string;
  type?: string;
}

interface IVitalDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  title: string;
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  fields: Field[];
  additionalContent?: React.ReactNode;
  isCurrentUserStudentOrTutor: boolean;
  isEditMode?: any;
}

const VitalDialog: React.FC<IVitalDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  formData,
  setFormData,
  fields,
  additionalContent,
  isCurrentUserStudentOrTutor,
  isEditMode,
}) => {
  const initialErrors = {
    updatedOn: "",
    bpHigh: "",
    bpLow: "",
    respiratoryRate: "",
    oxygenSaturation: "",
    pulse: "",
    temperature: "",
    common: "",
  };
  const offsetErrorText = "Please enter valid input.";
  const [error, setError] = useState<typeof initialErrors>(initialErrors);
  const [offsetError, setOffsetError] = useState("");
  const clearErrors = () => {
    setError(initialErrors);
    setOffsetError("");
  };
  // formData["offset"] = formData.offset === 0 ? undefined : formData.offset;

  const handleSubmit = () => {
    let atLeastOneFieldFilled = false;
    let newErros = { ...initialErrors };

    fields.forEach((field) => {
      const value = formData[field.key];
      if (value !== null && value !== undefined && value !== "") {
        atLeastOneFieldFilled = true;
      }
    });

    if (!atLeastOneFieldFilled) {
      newErros.common = "Please enter at least one field.";
    }

    // Validate BP fields
    if (formData.bpLow || formData.bpHigh) {
      if (!formData.bpLow || !formData.bpHigh) {
        newErros.bpLow = "Enter both systolic and diastolic values.";
        newErros.bpHigh = "Enter both systolic and diastolic values.";
      }
    }
    fields.forEach((field) => {
      const value = formData[field.key];
      if (value !== null && value !== undefined && value !== "") {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return;
        const ValidateRange =
          AppConfigurationHelper.Instance().vitalValidateValueRanges;
        switch (field.key) {
          case "bpHigh":
            if (
              numericValue < ValidateRange?.bp.systolic[0] ||
              numericValue > ValidateRange?.bp.systolic[1]
            ) {
              newErros.bpHigh =
                "Vitals are outside the valid range. Please check the values.";
            }
            break;
          case "bpLow":
            if (
              numericValue < ValidateRange?.bp.diastolic[0] ||
              numericValue > ValidateRange?.bp.diastolic[1]
            ) {
              newErros.bpLow =
                "Vitals are outside the valid range. Please check the values.";
            }
            break;
          case "pulse":
            if (
              numericValue < ValidateRange?.heartRate[0] ||
              numericValue > ValidateRange?.heartRate[1]
            ) {
              newErros.pulse =
                "Vitals are outside the valid range. Please check the values.";
            }
            break;
          case "respiratoryRate":
            if (
              numericValue < ValidateRange?.respiratoryRate[0] ||
              numericValue > ValidateRange?.respiratoryRate[1]
            ) {
              newErros.respiratoryRate =
                "Vitals are outside the valid range. Please check the values.";
            }
            break;
          case "oxygenSaturation":
            if (
              numericValue < ValidateRange?.oxygenSaturation[0] ||
              numericValue > ValidateRange?.oxygenSaturation[1]
            ) {
              newErros.oxygenSaturation =
                "Vitals are outside the valid range. Please check the values.";
            }
            break;
          case "temperature":
            if (
              numericValue < ValidateRange?.temperature[0] ||
              numericValue >
                AppConfigurationHelper.Instance().vitalValidateValueRanges
                  ?.temperature[1]
            ) {
              newErros.temperature =
                "Vitals are outside the valid range. Please check the values.";
            }
            break;
          default:
            break;
        }
      }
    });
    setError(newErros);
    setOffsetError(formData["offset"] === 0 ? offsetErrorText : "");
    const hasErrors = Object.values(newErros).some((msg) => msg.length > 0);
    if (hasErrors || formData["offset"] === 0) {
      return;
    }

    onSubmit();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    const value = e.target.value;
    setError(initialErrors);
    setOffsetError("");

    if (value === "") {
      setFormData((prevState: any) => ({ ...prevState, [field]: null }));
      setError((prevError) => {
        const updatedError = { ...prevError, [field]: "" };
        if (field === "bpLow" || field === "bpHigh") {
          if (!formData.bpLow || formData.bpHigh) {
            updatedError.bpLow = "";
            updatedError.bpHigh = "";
          }
        }
        return updatedError;
      });
      return;
    }

    if (/[^0-9.]/.test(value)) {
      return;
    }
    if ((value.match(/\./g) || []).length > 1) {
      return; // Reject if more than one decimal is found
    }

    if (value.includes("-")) {
      return; // Reject input if it contains a hyphen
    }
    setFormData((prevState: any) => ({ ...prevState, [field]: value }));

    if (RegularExpressions.NUMBER_WITH_TWO_DECIMAL.test(value)) {
      setError((prevError) => ({
        ...prevError,
        [field]: "",
      }));
    }

    if (field === "bpLow" || field === "bpHigh") {
      if (formData.bpLow && formData.bpHigh) {
        setError((prevError) => ({
          ...prevError,
          bpLow: "",
          bpHigh: "",
        }));
      }
    }
  };

  useEffect(() => {
    if (!open) {
      clearErrors();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle className="vital-title">
        {title}
        <IconButton
          edge="end"
          onClick={onClose}
          aria-label="close"
          sx={{
            position: "absolute",
            right: 25,
            top: 8,
            color: "#424242",
          }}
        >
          <GridCloseIcon />
        </IconButton>
        <Box
          sx={{
            borderBottom: "2px solid var(--color-border-model)",
            marginTop: 1.4,
          }}
        />
        <Box sx={{ marginTop: "10px", fontSize: "16px" }}>
          {isCurrentUserStudentOrTutor ? (
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                fontWeight: 600,
              }}
            >
              <Heading type="body1" text="Date & Time:" font="16px" />
              <span
                style={{
                  color: "var(--color-secondary-contrastText)",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                &nbsp;
                <span>
                  {DateFormatter.formatTimeWithoutMeridiem(
                    new Date().toISOString()
                  )}
                </span>
              </span>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                width: "240px",
              }}
            >
              <NewOffsetInput
                isSingleLineLayout={true}
                errorMessage={offsetError}
                onValueChanged={(value) => {
                  setOffsetError("");
                  setFormData((prevState: any) => ({
                    ...prevState,
                    offset: value,
                  }));
                }}
                value={formData["offset"]}
                isRequired={false}
                onlyHighlightOnError={false}
              />
            </div>
          )}
          <span>{additionalContent}</span>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, mt: "10px" }}>
          {fields.map((field) => (
            <Input
              data-testid="new-offset-input"
              name=""
              key={field.key}
              label={field.label}
              value={formData[field.key]}
              hasError={error[field.key as keyof typeof error]}
              onChange={(e) => handleChange(e, field.key)}
            />
          ))}
        </Box>
        <Box
          sx={{
            borderBottom: "2px solid var(--color-border-model)",
            marginTop: "21px",
          }}
        >
          <span style={{ color: "var(--color-error-main)" }}>
            {error.common}
          </span>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "0px 22px 15px 0px" }}>
        <Button
          text="Cancel"
          fullWidth={false}
          variant="outlined"
          type="button"
          onClick={onClose}
        />
        <Button
          text={isEditMode ? "Update" : "Save"}
          fullWidth={false}
          className="ml-1"
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default VitalDialog;
